import { action } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';
import { ClickTracker } from 'bernie-analytics';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { Level, liveAnnounce } from 'uitk-react-utils';
import { ToolbarType, UitkToolbar } from 'uitk-react-toolbar';
import { UitkButtonSize, UitkPrimaryFloatingButton, UitkTertiaryButton } from 'uitk-react-button';
import { UitkDialogAnimationType, UitkFullscreenDialog } from 'uitk-react-dialog';
import { UitkScrim, UitkScrimType } from 'uitk-react-scrim';

import { Room } from './internals/room';
import { RoomPickerModel } from 'components/common/room-picker/room-picker-model';

const SubmitButtonTracked = ClickTracker(UitkPrimaryFloatingButton);
const TertiaryButtonTracked = ClickTracker(UitkTertiaryButton);

export interface ChildAgeSelectOptions {
  value: string;
  name: string;
}

export interface RoomPickerProps extends LocalizedComponentProps {
  adultsString: string;
  analytics: AnalyticsStore;
  buttonText: string;
  childrenString: string;
  onDismiss?: () => void;
  onSave?: (adults: string, children: string, value: string) => void;
}

export interface RoomPickerState {
  dialogShow: boolean;
  selection: RoomPickerModel;
  justAddedRoom: boolean;
}

@withLocalization
@observer
export class RoomPicker extends React.Component<RoomPickerProps, RoomPickerState> {
  private focusOnOpen: React.RefObject<HTMLButtonElement> = React.createRef();
  private lastRoomHeader: React.RefObject<HTMLHeadingElement> = React.createRef();

  constructor(props) {
    super(props);

    this.state = {
      dialogShow: false,
      selection: new RoomPickerModel(props, props.l10n),
      justAddedRoom: false
    };

    this.onAddRoom = this.onAddRoom.bind(this);
    this.onChildAgeUpdated = this.onChildAgeUpdated.bind(this);
    this.onRemoveRoom = this.onRemoveRoom.bind(this);
    this.onRoomAdultsChanged = this.onRoomAdultsChanged.bind(this);
    this.onRoomChildrenChanged = this.onRoomChildrenChanged.bind(this);
    this.onDismiss = this.onDismiss.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.resetLiveAnnounce = this.resetLiveAnnounce.bind(this);
  }

  public static defaultRoomProps = {
    adultsString: '2',
    childrenString: ''
  };

  public componentWillMount() {
    this.updateStateWithProps(this.props);
  }

  public componentWillReceiveProps(nextProps) {
    this.updateStateWithProps(nextProps);
  }

  public componentDidMount() {
    this.setState({ dialogShow: true });
  }

  public componentDidUpdate() {
    this.setFocusOnLastRoomHeader();
  }

  public setFocusOnLastRoomHeader() {
    const isLastRoomHeaderFocusable = this.lastRoomHeader.current && typeof this.lastRoomHeader.current.focus === 'function';
    if (isLastRoomHeaderFocusable && this.state.justAddedRoom) {
      this.lastRoomHeader.current.focus();
      this.setState({ justAddedRoom: false });
    }
  }

  private updateStateWithProps(props: RoomPickerProps) {
    const { adults, children } = this.state.selection.initialProps;

    if (adults !== props.adultsString || children !== props.childrenString) {
      this.state.selection.setupRooms(props.adultsString, props.childrenString);
    }
  }

  public render() {
    const { analytics, l10n } = this.props;
    const { dialogShow, selection } = this.state;
    const { disableAddRoom, displaySummary, rooms } = selection;

    const toolBarAttributes = {
      iconLabel: l10n.formatText('hotels.roomPicker.cancelButton.a11yLabel'),
      header: l10n.formatText('hotels.roomPicker.title'),
      type: ToolbarType.CLOSE
    };

    const toolbarAnalytics = this.props.analytics.setupTracker(':ChangeRoomGuest', 'Close Room Picker');

    const submitActionText: string = this.props.buttonText ? this.props.buttonText : l10n.formatText('hotels.roomPicker.doneButton.label');

    const submitButtonText: JSX.Element = (
      <span>
        {submitActionText}
        <span className="is-visually-hidden">{l10n.formatText('hotels.roomPicker.doneButton.a11yLabel')}</span>
      </span>
    );

    const pickerMainClasses: string[] = [
      'uitk-cell',
      'all-x-padding-four',
      'all-t-padding-half',
      'all-b-padding-twelve',
      'uitk-scroll-vertical'
    ];

    return (
      <UitkFullscreenDialog
        animation={UitkDialogAnimationType.SLIDEUP}
        className="uitk-grid uitk-grid-cell all-cell-1-1"
        dialogShow={dialogShow}
      >
        <div className="uitk-cell">
          <UitkToolbar {...toolBarAttributes} action={this.onDismiss} analytics={toolbarAnalytics} buttonRef={this.focusOnOpen} />
        </div>
        <section className={pickerMainClasses.join(' ')}>
          {rooms.map((room, roomIndex) => (
            <Room
              analytics={analytics}
              key={roomIndex}
              l10n={l10n}
              headerRef={this.lastRoomHeader}
              onChildAgeUpdated={this.onChildAgeUpdated}
              onRoomAdultsChanged={this.onRoomAdultsChanged}
              onRoomChildrenChanged={this.onRoomChildrenChanged}
              onRemoveRoom={this.onRemoveRoom}
              room={room}
              roomIndex={roomIndex}
              selection={selection}
            />
          ))}
          <div className="uitk-type-right all-t-padding-four all-b-padding-twelve">
            <TertiaryButtonTracked
              data-stid="add-room"
              linkName="Add room"
              onClick={this.onAddRoom}
              disabled={disableAddRoom}
              rfrr=":AddRoom"
              text={l10n.formatText('hotels.roomPicker.addRoomButton.label')}
            />
          </div>
          <div className="all-t-padding-two all-b-padding-six" />
        </section>
        <UitkScrim type={UitkScrimType.BUTTON}>
          <SubmitButtonTracked
            isFullWidth={true}
            size={UitkButtonSize.LARGE}
            data-stid="apply-room-picker"
            linkName="Apply Room Picker"
            onClick={this.onSubmit}
            rfrr=":ChangeRoomGuestDone"
            text={submitButtonText}
            subText={displaySummary}
          />
        </UitkScrim>
      </UitkFullscreenDialog>
    );
  }

  @action
  private onRoomAdultsChanged(room, e): void {
    const num: number = parseInt(e.target.value, 10);

    if (!isNaN(num)) {
      this.state.selection.changeAdultsForRoom(room, num);
    }
  }

  @action
  private onRoomChildrenChanged(room, e): void {
    const num: number = parseInt(e.target.value, 10);
    if (!isNaN(num)) {
      this.state.selection.changeChildrenForRoom(room, num);
    }
  }

  @action
  private onAddRoom(): void {
    const { l10n } = this.props;
    const { selection } = this.state;
    const { roomCount } = selection;

    this.state.selection.addRoom();
    liveAnnounce(l10n.formatText('hotels.roomPicker.roomAdded.liveAnnounce', roomCount), Level.POLITE);
    this.setState({ justAddedRoom: true });
  }

  @action
  private onRemoveRoom(room): void {
    const { l10n } = this.props;
    const { selection } = this.state;
    const { roomCount } = selection;

    this.state.selection.removeRoom(room);
    liveAnnounce(l10n.formatText('hotels.roomPicker.roomRemoved.liveAnnounce', roomCount), Level.POLITE);
  }

  @action
  private onChildAgeUpdated(roomIndex: number, childIndex: number, target) {
    const { analytics, l10n } = this.props;
    const age: number = target.value;

    this.state.selection.changeChildAge(roomIndex, childIndex, age);
    target.focus();
    liveAnnounce(l10n.formatText('hotels.roomPicker.childUpdated.liveAnnounce', childIndex, age), Level.POLITE);

    if (analytics && typeof analytics.trackEvent === 'function') {
      const roomNum: number = roomIndex + 1;
      const childNum: number = childIndex + 1;
      analytics.trackEvent(`:ROOM.${roomNum}.Child.${childNum}.AGE.${age}`, 'Child age update', analytics.omnitureData);
    }
  }

  private resetLiveAnnounce(): void {
    liveAnnounce('', Level.ASSERTIVE);
  }

  private onDismiss(): void {
    const { onDismiss } = this.props;

    this.state.selection.reset();
    this.resetLiveAnnounce();

    if (onDismiss) {
      onDismiss();
    }
  }

  private onSubmit(): void {
    const { onSave } = this.props;

    if (onSave) {
      const { finalProps, displaySummary } = this.state.selection;

      onSave(finalProps.adults, finalProps.children, displaySummary);
    }

    this.resetLiveAnnounce();
  }
}
